<template>
  <div class="p-24">
    <div class="mb-24 h-[218px] overflow-hidden rounded-md">
      <RevIllustration
        alt=""
        class="h-full w-full object-cover"
        :height="218"
        role="presentation"
        sizes="327px"
        src="/img/product/jp/smartphone_battery.jpg"
        :width="327"
      />
    </div>

    <h2 class="heading-2 mb-16">
      {{ i18n(translations.bodyHeading) }}
    </h2>

    <p class="body-2 text-static-default-mid mb-24">
      {{ i18n(translations.description) }}
    </p>

    <div class="grid auto-rows-fr gap-12 mb-24">
      <div
        v-for="tier in batteryTiers"
        :key="tier.title.id"
        class="rounded-lg border border-static-default-low px-20 py-16 flex flex-col justify-center gap-8"
      >
        <span class="body-1-bold">{{ i18n(tier.title) }}</span>
        <span class="body-2 text-static-default-mid">{{
          i18n(tier.description)
        }}</span>
      </div>
    </div>

    <RevInfoBlock
      class="mb-24"
      :icon="IconInfo"
      :title="i18n(translations.infoTitle)"
      variant="default"
    >
      <p>{{ i18n(translations.infoContent) }}</p>
      <RevLink v-if="finalLink" target="_blank" :to="finalLink">{{
        i18n(translations.learnMore)
      }}</RevLink>
    </RevInfoBlock>

    <BatteryFaq :has-external-borders="false" />
  </div>
</template>

<script setup lang="ts">
import { useRouteLocationWithLocale } from '#imports'
import { computed } from 'vue'

import type { LinkInternal } from '@backmarket/http-api/src/api-models/LinkInternal'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevIllustration } from '@ds/components/Illustration'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevLink } from '@ds/components/Link'
import { IconInfo } from '@ds/icons/IconInfo'
import type { Location } from '@ds/types/Location'

import { LINKS } from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/NewBatteryModal/NewBatteryModal.constants'
import BatteryFaq from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/NewBatteryModal/components/BatteryFaq/BatteryFaq.vue'

import translations from '../../AboutBatteryModalThreeLevels.translations'

const props = defineProps<{
  link?: Location | null
}>()

const i18n = useI18n()
const {
  market: { countryCode },
} = useMarketplace()
const resolveLocalizedRoute = useRouteLocationWithLocale()

const batteryTiers = [
  {
    title: translations.batteryTier1Title,
    description: translations.batteryTier1Description,
  },
  {
    title: translations.batteryTier2Title,
    description: translations.batteryTier2Description,
  },
  {
    title: translations.batteryTier3Title,
    description: translations.batteryTier3Description,
  },
]

// When used as webview for apps, the link is not passed as prop. For now,
// we replicate the logic here but this should likely be handled externally.
// Something that can be improved under scope of PPI-7883.
const finalLink = computed(() => {
  return (
    props.link ??
    (LINKS[countryCode]
      ? resolveLocalizedRoute(LINKS[countryCode] as LinkInternal)
      : null)
  )
})
</script>
